import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner, { AdInFeed } from "@/components/feature/AdBanner";
import MercadoTicker from "@/components/feature/MercadoTicker";
import { useNews } from "@/hooks/useSupabaseData";
import { useAnalytics } from "@/hooks/useAnalytics";
import { usePageSeo } from "@/hooks/usePageSeo";
import { supabase } from "@/lib/supabase";

const statusFilters = [
  "Todas",
  "Negociação avançada",
  "Sondagem",
  "Contratado",
  "Proposta recusada",
];

type Transferencia = {
  id: number;
  titulo: string | null;
  jogador_nome: string;
  posicao: string | null;
  clube_origem: string | null;
  clube_origem_escudo: string | null;
  clube_destino: string | null;
  clube_destino_escudo: string | null;
  status: string;
  imagem_jogador: string | null;
  destaque: boolean | null;
  ativo: boolean | null;
  ordem: number | null;
  resumo: string | null;
  link_noticia: string | null;
  cor_status: string | null;
  data_movimentacao: string | null;
};

const fallbackImage =
  "https://readdy.ai/api/search-image?query=Football%20player%20transfer%20contract%20signing%20professional%20photography%20dramatic%20lighting%20stadium%20background&width=800&height=600&seq=mercado&orientation=landscape";

function getMercadoNewsPath(link: string) {
  const trimmedLink = link.trim();

  if (!trimmedLink) return null;

  if (trimmedLink.startsWith("/")) {
    return trimmedLink;
  }

  try {
    const url = new URL(trimmedLink);
    const isCentralAlvinegra = url.hostname.replace(/^www\./, "") === "centralalvinegra.com.br";

    if (isCentralAlvinegra) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.href;
  } catch {
    return `/${trimmedLink.replace(/^\/+/, "")}`;
  }
}

export default function MercadoDaBolaPage() {
  const navigate = useNavigate();

  const [activeFilter, setActiveFilter] = useState("Todas");
  const [transferenciasPage, setTransferenciasPage] = useState(1);
  const [transferencias, setTransferencias] = useState<Transferencia[]>([]);
  const [loadingTransferencias, setLoadingTransferencias] = useState(true);

  const { news: allNews, loading } = useNews({
    category: "Mercado da Bola",
    limit: 50,
  });

  useAnalytics("/mercado-da-bola");
  usePageSeo({
    title: "Mercado da Bola do Botafogo | SAF, Danilo e reforcos",
    description: "Rumores, sondagens, contratacoes e propostas do Botafogo no mercado da bola. Noticias sobre SAF, Danilo, FogaoNet e bastidores do Glorioso.",
    keywords: "Botafogo, Mercado da Bola, SAF Botafogo, Danilo, FogaoNet, reforcos do Botafogo, contratacoes Botafogo",
  });

  useEffect(() => {
    async function carregarTransferencias() {
      setLoadingTransferencias(true);

      const { data } = await supabase
        .from("mercado_transferencias")
        .select("*")
        .eq("ativo", true)
        .order("ordem", { ascending: true })
        .order("data_movimentacao", { ascending: false });

      setTransferencias((data || []) as Transferencia[]);
      setLoadingTransferencias(false);
    }

    carregarTransferencias();
  }, []);

  const filteredTransferencias =
    activeFilter === "Todas"
      ? transferencias
      : transferencias.filter((item) => item.status === activeFilter);
  const transferenciasPerPage = 6;
  const transferenciasTotalPages = Math.ceil(filteredTransferencias.length / transferenciasPerPage);
  const paginatedTransferencias = filteredTransferencias.slice(
    (transferenciasPage - 1) * transferenciasPerPage,
    transferenciasPage * transferenciasPerPage,
  );

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />
      <MercadoTicker />

      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />

      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumb
            items={[
              { label: "Início", path: "/" },
              { label: "Mercado da Bola" },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-4">
              Mercado da Bola
            </h1>

            <p className="text-alvinegro-muted text-base">
              Rumores, sondagens, negociações e transferências envolvendo o Botafogo.
            </p>
          </div>

          <div className="mb-6">
            <AdBanner variant="leaderboard" label="Publicidade" />
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {statusFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  setActiveFilter(filter);
                  setTransferenciasPage(1);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeFilter === filter
                    ? "bg-alvinegro-black text-white"
                    : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <section className="mb-10">
            <div className="flex items-center gap-2 mb-5">
              <i className="ri-exchange-line text-alvinegro-black text-xl" />
              <h2 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">
                Rumores e Transações
              </h2>
            </div>

            {loadingTransferencias ? (
              <div className="flex gap-4 overflow-hidden">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="min-w-[260px] h-40 bg-alvinegro-light rounded-2xl animate-pulse"
                  />
                ))}
              </div>
            ) : filteredTransferencias.length === 0 ? (
              <div className="bg-alvinegro-light rounded-2xl p-6 text-sm text-alvinegro-muted">
                Nenhuma movimentação cadastrada para este filtro.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {paginatedTransferencias.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (item.link_noticia) {
                        const newsPath = getMercadoNewsPath(item.link_noticia);

                        if (!newsPath) return;

                        if (newsPath.startsWith("http")) {
                          window.open(newsPath, "_blank", "noopener,noreferrer");
                          return;
                        }

                        navigate(newsPath);
                      }
                    }}
                    className="bg-white border border-alvinegro-border rounded-2xl overflow-hidden text-left hover:shadow-lg transition-shadow"
                  >
                    <div className="relative h-36 bg-alvinegro-light overflow-hidden">
                      <img
                        src={item.imagem_jogador || fallbackImage}
                        alt={item.jogador_nome}
                        className="w-full h-full object-cover"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                      <span
                        className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: item.cor_status || "#3b82f6" }}
                      >
                        {item.status}
                      </span>

                      {item.destaque && (
                        <span className="absolute top-3 right-3 bg-white text-alvinegro-black px-2.5 py-1 rounded-full text-xs font-bold">
                          Destaque
                        </span>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-bold text-alvinegro-black line-clamp-1">
                        {item.jogador_nome}
                      </h3>

                      <p className="text-xs text-alvinegro-muted mt-1">
                        {item.posicao || "Posição não informada"}
                      </p>

                      <div className="flex items-center gap-2 mt-3 text-xs text-alvinegro-dark">
                        {item.clube_origem_escudo && (
                          <img
                            src={item.clube_origem_escudo}
                            alt={item.clube_origem || ""}
                            className="w-6 h-6 rounded-full object-contain bg-white border border-alvinegro-border"
                          />
                        )}

                        <span className="truncate">{item.clube_origem || "Livre"}</span>

                        <i className="ri-arrow-right-line text-alvinegro-muted" />

                        <span className="truncate">
                          {item.clube_destino || "Indefinido"}
                        </span>

                        {item.clube_destino_escudo && (
                          <img
                            src={item.clube_destino_escudo}
                            alt={item.clube_destino || ""}
                            className="w-6 h-6 rounded-full object-contain bg-white border border-alvinegro-border"
                          />
                        )}
                      </div>

                      {item.resumo && (
                        <p className="text-xs text-alvinegro-muted mt-3 line-clamp-2">
                          {item.resumo}
                        </p>
                      )}
                    </div>
                  </button>
                  ))}
                </div>

                {transferenciasTotalPages > 1 && (
                  <div className="flex items-center justify-between gap-3 mt-5">
                    <p className="text-xs text-alvinegro-muted">
                      Mostrando {Math.min((transferenciasPage - 1) * transferenciasPerPage + 1, filteredTransferencias.length)}-{Math.min(transferenciasPage * transferenciasPerPage, filteredTransferencias.length)} de {filteredTransferencias.length}
                    </p>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setTransferenciasPage((page) => Math.max(1, page - 1))}
                        disabled={transferenciasPage === 1}
                        className="w-9 h-9 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line" />
                      </button>

                      {Array.from({ length: transferenciasTotalPages }, (_, index) => index + 1).map((page) => (
                        <button
                          key={page}
                          type="button"
                          onClick={() => setTransferenciasPage(page)}
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                            transferenciasPage === page
                              ? "bg-alvinegro-black text-white"
                              : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        type="button"
                        onClick={() => setTransferenciasPage((page) => Math.min(transferenciasTotalPages, page + 1))}
                        disabled={transferenciasPage === transferenciasTotalPages}
                        className="w-9 h-9 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          <section>
            <div className="flex items-center gap-2 mb-5">
              <i className="ri-newspaper-line text-alvinegro-black text-xl" />
              <h2 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">
                Notícias do Mercado
              </h2>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 animate-pulse">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl overflow-hidden border border-alvinegro-border"
                  >
                    <div className="aspect-[4/3] bg-alvinegro-light" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-alvinegro-light rounded w-full" />
                      <div className="h-3 bg-alvinegro-light rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : allNews.length === 0 ? (
              <div className="bg-alvinegro-light rounded-2xl p-6 text-sm text-alvinegro-muted">
                Nenhuma notícia cadastrada na categoria Mercado da Bola.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {allNews.slice(0, 3).map((news) => (
                    <div
                      key={news.id}
                      className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-alvinegro-border hover:shadow-lg transition-shadow"
                      onClick={() => navigate(`/noticias/${news.slug}`)}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={news.image_url || fallbackImage}
                          alt={news.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        <span className="absolute top-3 right-3 bg-white/90 text-alvinegro-black text-xs font-semibold px-2.5 py-1 rounded-full">
                          Mercado
                        </span>
                      </div>

                      <div className="p-4">
                        <h3 className="font-semibold text-base text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                          {news.title}
                        </h3>

                        <p className="text-alvinegro-muted text-sm mt-1 line-clamp-2">
                          {news.subtitle}
                        </p>

                        <div className="flex items-center gap-2 mt-3 text-xs text-alvinegro-muted">
                          <span>
                            {news.published_at
                              ? new Date(news.published_at).toLocaleDateString("pt-BR")
                              : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {allNews.length > 3 && (
                  <>
                    <div className="my-6 md:my-8">
                      <AdInFeed />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                      {allNews.slice(3, 9).map((news) => (
                        <div
                          key={news.id}
                          className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-alvinegro-border hover:shadow-lg transition-shadow"
                          onClick={() => navigate(`/noticias/${news.slug}`)}
                        >
                          <div className="relative aspect-[4/3] overflow-hidden">
                            <img
                              src={news.image_url || fallbackImage}
                              alt={news.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                            <span className="absolute top-3 right-3 bg-white/90 text-alvinegro-black text-xs font-semibold px-2.5 py-1 rounded-full">
                              Mercado
                            </span>
                          </div>

                          <div className="p-4">
                            <h3 className="font-semibold text-base text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                              {news.title}
                            </h3>

                            <p className="text-alvinegro-muted text-sm mt-1 line-clamp-2">
                              {news.subtitle}
                            </p>

                            <div className="flex items-center gap-2 mt-3 text-xs text-alvinegro-muted">
                              <span>
                                {news.published_at
                                  ? new Date(news.published_at).toLocaleDateString("pt-BR")
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          <div className="mt-8">
            <AdBanner variant="medium-rectangle" label="Publicidade" />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
